import { useState } from "react";
import type { FormEvent } from "react";
import type { CommentItem } from "@/types/content";

interface CommentSectionProps {
  initial: CommentItem[];
}

function formatNow() {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function countAll(items: CommentItem[]): number {
  return items.reduce((total, item) => total + 1 + (item.replies ? countAll(item.replies) : 0), 0);
}

function applyLike(items: CommentItem[], id: string, isLiked: boolean): CommentItem[] {
  return items.map((item) => {
    if (item.id === id) {
      return { ...item, likes: item.likes + (isLiked ? -1 : 1) };
    }
    if (item.replies && item.replies.length > 0) {
      return { ...item, replies: applyLike(item.replies, id, isLiked) };
    }
    return item;
  });
}

export default function CommentSection({ initial }: CommentSectionProps) {
  const [list, setList] = useState<CommentItem[]>(initial);
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState("");

  const toggleLike = (id: string) => {
    const isLiked = Boolean(liked[id]);
    setLiked((prev) => ({ ...prev, [id]: !isLiked }));
    setList((prev) => applyLike(prev, id, isLiked));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = content.trim();
    if (!text) return;
    const name = author.trim() || "匿名网友";
    const newComment: CommentItem = {
      id: `local-${Date.now()}`,
      author: name,
      createdAt: formatNow(),
      content: text,
      likes: 0,
    };
    if (replyTo) {
      setList((prev) =>
        prev.map((item) =>
          item.id === replyTo.id
            ? { ...item, replies: [...(item.replies ?? []), newComment] }
            : item,
        ),
      );
    } else {
      setList((prev) => [newComment, ...prev]);
    }
    setContent("");
    setAuthor("");
    setReplyTo(null);
    setNotice("留言已发布，感谢你参与讨论。");
    window.setTimeout(() => setNotice(""), 3200);
  };

  return (
    <section id="comments" aria-label="评论区互动留言" className="mt-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl text-foreground-950 md:text-2xl">
          评论区互动
          <span className="ml-2 font-label text-sm font-normal text-foreground-500">
            {countAll(list)} 条留言
          </span>
        </h2>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900">
          <i className="ri-user-forbid-line" aria-hidden="true" />
          支持匿名昵称
        </span>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
      >
        {replyTo ? (
          <div className="mb-3 flex items-center justify-between rounded-md bg-accent-100 px-3 py-2 font-label text-xs text-accent-900">
            <span>正在回复 {replyTo.name}</span>
            <button type="button" onClick={() => setReplyTo(null)} className="hover:text-accent-700">
              取消
            </button>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <label htmlFor="comment-author" className="sr-only">
              你的昵称
            </label>
            <input
              id="comment-author"
              type="text"
              value={author}
              onChange={(event) => setAuthor(event.target.value)}
              maxLength={20}
              placeholder="昵称（可留空，默认匿名网友）"
              className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 sm:max-w-xs"
            />
          </div>
          <label htmlFor="comment-content" className="sr-only">
            留言内容
          </label>
          <textarea
            id="comment-content"
            value={content}
            onChange={(event) => setContent(event.target.value.slice(0, 200))}
            rows={4}
            placeholder="说点什么吧——补充细节、提出疑问，或者讲讲你遇到过的类似情况。"
            className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-label text-xs text-foreground-500">
            {content.length}/200 · 留言会即时显示在下方
          </p>
          <div className="flex items-center gap-3">
            {notice ? (
              <span className="inline-flex items-center gap-1 font-label text-xs text-primary-700">
                <i className="ri-checkbox-circle-line" aria-hidden="true" />
                {notice}
              </span>
            ) : null}
            <button
              type="submit"
              disabled={!content.trim()}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-send-plane-2-line mr-1.5" aria-hidden="true" />
              {replyTo ? "发布回复" : "发布留言"}
            </button>
          </div>
        </div>
      </form>

      <ul className="mt-6 flex flex-col gap-5">
        {list.map((item) => (
          <li key={item.id} className="flex gap-3 md:gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-200 text-secondary-800">
              <i className="ri-user-3-line" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1 rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-label text-sm font-medium text-foreground-900">{item.author}</span>
                <span className="font-label text-xs text-foreground-500">{item.createdAt}</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.content}</p>
              <div className="mt-3 flex items-center gap-4 font-label text-xs text-foreground-500">
                <button
                  type="button"
                  onClick={() => toggleLike(item.id)}
                  className={`inline-flex items-center gap-1 transition-colors ${
                    liked[item.id] ? "text-primary-600" : "hover:text-primary-600"
                  }`}
                >
                  <i className={liked[item.id] ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
                  {item.likes}
                </button>
                <button
                  type="button"
                  onClick={() => setReplyTo({ id: item.id, name: item.author })}
                  className="inline-flex items-center gap-1 transition-colors hover:text-primary-600"
                >
                  <i className="ri-reply-line" aria-hidden="true" />
                  回复
                </button>
              </div>

              {item.replies && item.replies.length > 0 ? (
                <ul className="mt-4 flex flex-col gap-3 border-l-2 border-background-200 pl-4">
                  {item.replies.map((reply) => (
                    <li key={reply.id}>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="font-label text-sm font-medium text-foreground-900">
                          {reply.author}
                        </span>
                        <span className="font-label text-xs text-foreground-500">{reply.createdAt}</span>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{reply.content}</p>
                      <button
                        type="button"
                        onClick={() => toggleLike(reply.id)}
                        className={`mt-2 inline-flex items-center gap-1 font-label text-xs transition-colors ${
                          liked[reply.id] ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                        }`}
                      >
                        <i className={liked[reply.id] ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
                        {reply.likes}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}